/** Another element whose class must flip in the same step, e.g. a child widget's host. */
export interface LinkedClass {
  el: HTMLElement | null | undefined;
  className: string;
}

const NO_ANIM = 'condense-sticky--no-anim';

/** One global rule, so it reaches inside encapsulated child components too. */
function ensureNoAnimRule(): void {
  if (document.getElementById(NO_ANIM)) {
    return;
  }
  const style = document.createElement('style');
  style.id = NO_ANIM;
  style.textContent = `.${NO_ANIM}, .${NO_ANIM} * { transition: none !important; }`;
  document.head.appendChild(style);
}

/**
 * Toggles the condensed class on an in-flow sticky bar without moving anything
 * after it: the height the bar loses is given back as margin-bottom, so its
 * footprint in the page never changes.
 *
 * Without this the content below jumps by the lost height (CLS), and Chrome's
 * scroll anchoring scrolls the page back by the same amount, which takes the
 * bar back past its threshold and un-condenses it: a stick/unstick flicker loop.
 *
 * Transitions inside the bar are paused for the toggle (an animated height
 * cannot be measured, and would shift the content frame by frame), and scroll
 * anchoring is paused while the uncompensated layout is measured.
 */
export function setCondensed(
  el: HTMLElement,
  className: string,
  condensed: boolean,
  linked: LinkedClass[] = []
): void {
  if (el.classList.contains(className) === condensed) {
    return;
  }
  ensureNoAnimRule();
  const scrollers = [document.documentElement, document.body];
  const anchoring = scrollers.map(s => s.style.overflowAnchor);
  scrollers.forEach(s => (s.style.overflowAnchor = 'none'));
  el.classList.add(NO_ANIM);
  try {
    const expanded = el.offsetHeight;
    el.classList.toggle(className, condensed);
    linked.forEach(l => l.el?.classList.toggle(l.className, condensed));
    el.style.marginBottom = '';
    if (condensed) {
      const lost = expanded - el.offsetHeight;
      if (lost > 0) {
        const base = parseFloat(getComputedStyle(el).marginBottom) || 0;
        el.style.marginBottom = `${base + lost}px`;
      }
    }
    // Settle the final layout before transitions and anchoring are back. A
    // call, not a bare property read: production minifiers drop `void el.offsetHeight`.
    el.getBoundingClientRect();
  } finally {
    el.classList.remove(NO_ANIM);
    scrollers.forEach((s, i) => (s.style.overflowAnchor = anchoring[i]));
  }
}
